import { useQuery } from '@tanstack/react-query'
import { api } from './client'
export interface GiphyConfig { enabled: boolean; api_key?: string; rating?: string }
export interface Gif { id: string; title: string; images: { fixed_height: { url: string }; original: { url: string } } }
export function useGiphyConfig() {
  return useQuery({ queryKey: ['giphy-config'], queryFn: ({ signal }) => api.get<GiphyConfig>('/integrations/giphy', { platform: 'web' }, signal), staleTime: 60_000, gcTime: 0, retry: false })
}
export function gifId(text: string): string | null { return /^https:\/\/giphy\.com\/gifs\/([A-Za-z0-9]{1,100})$/.exec(text.trim())?.[1] ?? null }
export function gifLink(id: string): string { if (!/^[A-Za-z0-9]{1,100}$/.test(id)) throw new Error('Invalid GIF'); return `https://giphy.com/gifs/${id}` }
/** Standalone ID links can accompany captions; fenced/indented code stays literal. */
export function gifMessageParts(text: string): string[] {
  const parts: string[] = [], pending: string[] = []
  let fence = ''
  for (const line of text.split('\n')) {
    const marker = /^ {0,3}(`{3,}|~{3,})/.exec(line)?.[1]
    if (marker) {
      if (!fence) fence = marker
      else if (marker[0] === fence[0] && marker.length >= fence.length && line.trim() === marker) fence = ''
      pending.push(line)
    } else if (!fence && line.startsWith('https://') && gifId(line)) {
      if (pending.length) parts.push(pending.splice(0).join('\n'))
      parts.push(line)
    } else pending.push(line)
  }
  if (pending.length) parts.push(pending.join('\n'))
  return parts
}
export function giphyMedia(raw: string): string | null {
  try { const url = new URL(raw); return url.protocol === 'https:' && /^(?:media\d*|i)\.giphy\.com$/.test(url.hostname) && !url.username && !url.password ? raw : null } catch { return null }
}
export async function giphyFetch(config: GiphyConfig, query: string, signal: AbortSignal, id?: string): Promise<Gif[]> {
  if (!config.enabled || !config.api_key) throw new Error('GIPHY is not enabled for this Space.')
  const url = new URL(`https://api.giphy.com/v1/gifs/${id ? encodeURIComponent(id) : query.trim() ? 'search' : 'trending'}`)
  url.searchParams.set('api_key', config.api_key)
  if (!id) { url.searchParams.set('rating', 'g'); url.searchParams.set('limit', '20'); if (query.trim()) url.searchParams.set('q', query.trim().slice(0, 100)) }
  const response = await fetch(url, { signal, credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer' })
  if (!response.ok) throw new Error('GIPHY could not load. Please try again later.')
  const result = await response.json() as { data: Gif[] | Gif }
  const items = id ? [result.data as Gif] : result.data as Gif[]
  if (!Array.isArray(items) || items.length > 20 || items.some(item => !item || !/^[A-Za-z0-9]{1,100}$/.test(item.id) || typeof item.title !== 'string' || !giphyMedia(item.images?.fixed_height?.url) || !giphyMedia(item.images?.original?.url))) throw new Error('GIPHY returned an invalid response.')
  return items
}
